import { Controller } from "@hotwired/stimulus";

// Drives the "Link iMIS ID" modal on the admin user profile.
//
// Linking writes an identity onto an account, so the admin never confirms an ID
// they have only typed: the ID is looked up first and the matched iMIS contact is
// shown, and only then does Confirm appear. Editing the ID afterwards withdraws
// the confirmation and sends them back through the lookup.
export default class extends Controller {
  static targets = [
    "imisId", "error", "errorMessage",
    "match", "matchName", "matchEmail", "matchMemberType", "emailMismatch",
    "lookupBtn", "confirmBtn"
  ];

  static values = { previewUrl: String, hubEmail: String };

  connect() {
    this._boundShown = this._focusIdField.bind(this);
    this._boundHidden = this._reset.bind(this);

    this.element.addEventListener("shown.bs.modal", this._boundShown);
    this.element.addEventListener("hidden.bs.modal", this._boundHidden);
  }

  disconnect() {
    this.element.removeEventListener("shown.bs.modal", this._boundShown);
    this.element.removeEventListener("hidden.bs.modal", this._boundHidden);
  }

  onIdInput() {
    this._hideError();
    this._hideMatch();
    this.lookupBtnTarget.disabled = this._imisId() === "";
  }

  // Enter would otherwise submit the form and link whatever was typed, skipping
  // the whole point of the preview.
  onIdKeydown(event) {
    if (event.key !== "Enter") return;

    event.preventDefault();
    if (!this.lookupBtnTarget.disabled) this.lookup();
  }

  async lookup() {
    const imisId = this._imisId();
    if (imisId === "") return;

    this._hideError();
    this.lookupBtnTarget.disabled = true;

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ imis_id: imisId })
      });

      if (!response.ok) throw new Error(response.statusText);

      this._handlePreview(await response.json());
    } catch (error) {
      this._showError("Something went wrong. Please try again.");
    } finally {
      this.lookupBtnTarget.disabled = this._imisId() === "";
    }
  }

  // Forgery protection is off in the test environment, where csrf_meta_tags then
  // renders nothing — reading the token blindly would break the lookup there.
  _headers() {
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    if (token) headers["X-CSRF-Token"] = token;

    return headers;
  }

  _handlePreview(data) {
    if (!data.found) {
      this._showError(data.message);
      return;
    }

    if (data.conflict) {
      this._showError(data.conflict);
      return;
    }

    this._showMatch(data.profile);
  }

  _showMatch(profile) {
    this.matchNameTarget.textContent = profile.name || "—";
    this.matchEmailTarget.textContent = profile.email || "—";
    this.matchMemberTypeTarget.textContent = profile.member_type || "—";

    this._toggle(this.emailMismatchTarget, this._emailDiffers(profile.email));
    this._toggle(this.matchTarget, true);
    this._toggle(this.lookupBtnTarget, false);
    this._toggle(this.confirmBtnTarget, true);
    this.confirmBtnTarget.focus();
  }

  _hideMatch() {
    this._toggle(this.matchTarget, false);
    this._toggle(this.emailMismatchTarget, false);
    this._toggle(this.confirmBtnTarget, false);
    this._toggle(this.lookupBtnTarget, true);
  }

  _emailDiffers(imisEmail) {
    if (!imisEmail) return false;

    return imisEmail.trim().toLowerCase() !== this.hubEmailValue.trim().toLowerCase();
  }

  _reset() {
    this.imisIdTarget.value = "";
    this.lookupBtnTarget.disabled = true;
    this._hideError();
    this._hideMatch();
  }

  _focusIdField() {
    this.imisIdTarget.focus();
  }

  _imisId() {
    return this.imisIdTarget.value.trim();
  }

  _showError(message) {
    this.errorMessageTarget.textContent = message || "Could not look up that iMIS ID.";
    this._toggle(this.errorTarget, true);
    this._hideMatch();
  }

  _hideError() {
    this._toggle(this.errorTarget, false);
  }

  _toggle(element, show) {
    if (element) element.classList.toggle("d-none", !show);
  }
}
